import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, Globe, Facebook, Instagram, Linkedin, Twitter, Github } from 'lucide-react';
import { contactApi } from '../services/api';

type ContactField = {
  id: number;
  field_key: string;
  field_label: string;
  field_type: 'text' | 'email' | 'tel' | 'textarea' | 'select' | 'radio' | 'checkbox' | 'url' | 'number' | 'date' | 'time' | 'datetime-local' | 'password';
  is_required: boolean;
  placeholder?: string;
  options?: string;
  width?: 'full' | 'half' | 'third';
  display_order?: number;
};

type ContactSettings = {
  section_title?: string;
  section_subtitle?: string;
  section_description?: string;
  submit_button_text?: string;
  success_message?: string;
  error_message?: string;
  email_recipient?: string;
  smtp_user?: string;
  show_phone?: boolean;
  show_email?: boolean;
  show_address?: boolean;
  show_social?: boolean;
  details_badge_text?: string;
  details_heading?: string;
  details_description?: string;
  phone_number?: string;
  address?: string;
  social_links?: string;
  background_color?: string;
  text_color?: string;
  button_color?: string;
  button_text_color?: string;
  input_bg_color?: string;
  input_text_color?: string;
  input_border_color?: string;
};

type SocialLink = {
  platform: string;
  url: string;
};

const parseOptions = (options?: string): string[] => {
  if (!options) return [];
  try {
    const parsed = JSON.parse(options);
    return Array.isArray(parsed) ? parsed.map(String) : [];
  } catch {
    return options.split(',').map(s => s.trim()).filter(Boolean);
  }
};

const widthClass = (w?: 'full' | 'half' | 'third') => {
  if (w === 'third') return 'col-span-12 md:col-span-4';
  if (w === 'half') return 'col-span-12 md:col-span-6';
  return 'col-span-12';
};

const parseSocialLinks = (value?: string): SocialLink[] => {
  if (!value) return [];
  try {
    const parsed = JSON.parse(value);
    if (Array.isArray(parsed)) {
      return parsed
        .map((item) => ({
          platform: String(item?.platform || '').trim(),
          url: String(item?.url || '').trim(),
        }))
        .filter((item) => item.platform || item.url);
    }
    if (parsed && typeof parsed === 'object') {
      return Object.entries(parsed).map(([platform, url]) => ({
        platform: String(platform).trim(),
        url: String(url || '').trim(),
      })).filter((item) => item.platform || item.url);
    }
  } catch {
    void 0;
  }
  return [];
};

const getSocialIcon = (platform: string) => {
  const key = platform.trim().toLowerCase();
  if (key.includes('facebook')) return Facebook;
  if (key.includes('instagram')) return Instagram;
  if (key.includes('linkedin')) return Linkedin;
  if (key.includes('twitter') || key.includes('x')) return Twitter;
  if (key.includes('github')) return Github;
  return Globe;
};

const ContactFormPage: React.FC = () => {
  const [settings, setSettings] = useState<ContactSettings | null>(null);
  const [fields, setFields] = useState<ContactField[]>([]);
  const [formData, setFormData] = useState<Record<string, unknown>>({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState<{ type: 'idle' | 'success' | 'error'; message: string }>({ type: 'idle', message: '' });

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [sRes, fRes] = await Promise.all([
          contactApi.getPublicSettings(),
          contactApi.getPublicFields()
        ]);
        if (sRes?.success) setSettings(sRes.data || {});
        if (fRes?.success) {
          const sorted = [...(fRes.data || [])].sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
          setFields(sorted);
        }
      } catch {
        setFields([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const title = useMemo(() => settings?.section_title || 'Contact Us', [settings?.section_title]);
  const subtitle = useMemo(() => settings?.section_subtitle || 'Let us know your requirements', [settings?.section_subtitle]);
  const description = useMemo(() => settings?.section_description || 'Share details and we will get back to you shortly.', [settings?.section_description]);
  const detailsBadgeText = useMemo(() => settings?.details_badge_text || 'Contact Details', [settings?.details_badge_text]);
  const detailsHeading = useMemo(() => settings?.details_heading || 'Talk To Our Team', [settings?.details_heading]);
  const detailsDescription = useMemo(() => settings?.details_description || 'Share your requirements through the form, or reach out directly using the details below.', [settings?.details_description]);
  const socialLinks = useMemo(() => parseSocialLinks(settings?.social_links), [settings?.social_links]);
  const displayEmail = useMemo(() => settings?.email_recipient || settings?.smtp_user || '', [settings?.email_recipient, settings?.smtp_user]);
  const inputStyle = {
    backgroundColor: settings?.input_bg_color || '#12141c',
    color: settings?.input_text_color || '#ffffff',
    borderColor: settings?.input_border_color || 'rgba(255,255,255,0.1)',
  };
  const buttonStyle = {
    backgroundColor: settings?.button_color || '#FFBF00',
    color: settings?.button_text_color || '#ffffff',
  };

  const setValue = (key: string, value: unknown) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setStatus({ type: 'idle', message: '' });
    try {
      const res = await contactApi.submit(formData);
      if (res?.success) {
        setStatus({ type: 'success', message: res.message || settings?.success_message || 'Message sent successfully.' });
        setFormData({});
      } else {
        setStatus({ type: 'error', message: res?.message || settings?.error_message || 'Failed to submit form.' });
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : (settings?.error_message || 'Failed to submit form.');
      setStatus({ type: 'error', message: msg });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="min-h-screen bg-black text-white relative overflow-hidden pt-32 pb-24 px-4">
      <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
        <motion.div
          animate={{
            x: [0, 100, 0],
            y: [0, -100, 0]
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: 'linear'
          }}
          className="absolute top-[-10%] left-[-10%] w-[50vw] h-[50vw] bg-orange-900/10 blur-[120px] rounded-full"
        />
        <motion.div
          animate={{
            x: [0, -100, 0],
            y: [0, 100, 0]
          }}
          transition={{
            duration: 25,
            repeat: Infinity,
            ease: 'linear'
          }}
          className="absolute bottom-[-10%] right-[-10%] w-[50vw] h-[50vw] bg-amber-900/10 blur-[120px] rounded-full"
        />
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:100px_100px]" />
      </div>
      <div className="relative z-10 max-w-6xl mx-auto">
        <div className="mb-10 text-center">
          <h1 className="text-4xl md:text-5xl font-black tracking-tight">{title}</h1>
          <p className="text-amber-400 mt-2">{subtitle}</p>
          <p className="text-gray-400 mt-3">{description}</p>
        </div>

        <div className="grid gap-6 lg:grid-cols-[0.9fr_1.1fr]">
          <aside className="self-start rounded-2xl border border-white/10 bg-[#0b0c15] p-6 md:p-8">
            <div className="max-w-md">
              <p className="text-sm uppercase tracking-[0.35em] text-amber-400">{detailsBadgeText}</p>
              <h2 className="mt-4 text-3xl font-black">{detailsHeading}</h2>
              <p className="mt-4 text-gray-400">{detailsDescription}</p>
            </div>

            <div className="mt-8 space-y-4">
              {settings?.show_email !== false && displayEmail && (
                <a href={`mailto:${displayEmail}`} className="flex items-start gap-4 py-2 transition-colors hover:text-amber-300">
                  <span className="mt-1 rounded-xl bg-amber-500/15 p-3 text-amber-400"><Mail size={18} /></span>
                  <span>
                    <span className="block text-base font-semibold text-white">{displayEmail}</span>
                  </span>
                </a>
              )}
              {settings?.show_phone && settings?.phone_number && (
                <a href={`tel:${settings.phone_number}`} className="flex items-start gap-4 py-2 transition-colors hover:text-amber-300">
                  <span className="mt-1 rounded-xl bg-amber-500/15 p-3 text-amber-400"><Phone size={18} /></span>
                  <span>
                    <span className="block text-base font-semibold text-white">{settings.phone_number}</span>
                  </span>
                </a>
              )}
              {settings?.show_address && settings?.address && (
                <div className="flex items-start gap-4 py-2">
                  <span className="mt-1 rounded-xl bg-amber-500/15 p-3 text-amber-400"><MapPin size={18} /></span>
                  <span>
                    <span className="block whitespace-pre-line text-base font-semibold text-white">{settings.address}</span>
                  </span>
                </div>
              )}
            </div>

            {settings?.show_social && socialLinks.length > 0 && (
              <div className="mt-8">
                <p className="text-sm font-semibold text-white">Social Links</p>
                <div className="mt-4 flex flex-wrap gap-3">
                  {socialLinks.map((link, index) => {
                    const Icon = getSocialIcon(link.platform);
                    return (
                      <a
                        key={`${link.platform}-${index}`}
                        href={link.url || '#'}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center justify-center rounded-full border border-white/10 bg-white/[0.03] p-3 text-sm text-white transition-colors hover:border-amber-500/40 hover:text-amber-300"
                        aria-label={link.platform || 'Social link'}
                        title={link.platform || 'Social link'}
                      >
                        <Icon size={16} />
                      </a>
                    );
                  })}
                </div>
              </div>
            )}
          </aside>

          <div className="rounded-2xl border border-white/10 bg-[#0b0c15] p-6 md:p-8">
            {loading ? (
              <div className="py-20 text-center text-gray-400">Loading form...</div>
            ) : (
              <form onSubmit={submit} className="grid grid-cols-12 gap-4">
              {fields.map((field) => {
                const opts = parseOptions(field.options);
                const val = formData[field.field_key];
                const cls = `w-full rounded-lg border px-4 py-3 outline-none focus:border-amber-500`;
                return (
                  <div key={field.id || field.field_key} className={widthClass(field.width)}>
                    <label className="block text-sm text-gray-300 mb-2">
                      {field.field_label} {field.is_required ? '*' : ''}
                    </label>
                    {field.field_type === 'textarea' && (
                      <textarea
                        className={cls}
                        style={inputStyle}
                        rows={5}
                        placeholder={field.placeholder || ''}
                        required={field.is_required}
                        value={String(val ?? '')}
                        onChange={(e) => setValue(field.field_key, e.target.value)}
                      />
                    )}
                    {field.field_type === 'select' && (
                      <select
                        className={cls}
                        style={inputStyle}
                        required={field.is_required}
                        value={String(val ?? '')}
                        onChange={(e) => setValue(field.field_key, e.target.value)}
                      >
                        <option value="">{field.placeholder || 'Select an option'}</option>
                        {opts.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                      </select>
                    )}
                    {field.field_type === 'radio' && (
                      <div className="flex flex-wrap gap-3">
                        {opts.map(opt => (
                          <label key={opt} className="text-sm text-gray-300">
                            <input
                              type="radio"
                              name={field.field_key}
                              className="mr-2"
                              checked={String(val ?? '') === opt}
                              onChange={() => setValue(field.field_key, opt)}
                            />
                            {opt}
                          </label>
                        ))}
                      </div>
                    )}
                    {field.field_type === 'checkbox' && (
                      <label className="text-sm text-gray-300">
                        <input
                          type="checkbox"
                          className="mr-2"
                          checked={Boolean(val)}
                          onChange={(e) => setValue(field.field_key, e.target.checked)}
                        />
                        {field.placeholder || field.field_label}
                      </label>
                    )}
                    {!['textarea', 'select', 'radio', 'checkbox'].includes(field.field_type) && (
                      <input
                        type={field.field_type}
                        className={cls}
                        style={inputStyle}
                        placeholder={field.placeholder || ''}
                        required={field.is_required}
                        value={String(val ?? '')}
                        onChange={(e) => setValue(field.field_key, e.target.value)}
                      />
                    )}
                  </div>
                );
              })}

              {status.type !== 'idle' && (
                <div className={`col-span-12 rounded-lg px-4 py-3 text-sm ${status.type === 'success' ? 'bg-green-500/10 text-green-400 border border-green-500/30' : 'bg-red-500/10 text-red-400 border border-red-500/30'}`}>
                  {status.message}
                </div>
              )}

              <div className="col-span-12">
                <button
                  type="submit"
                  disabled={submitting || loading}
                  className="w-full rounded-lg disabled:opacity-60 px-5 py-3 font-semibold transition-opacity"
                  style={buttonStyle}
                >
                  {submitting ? 'Submitting...' : (settings?.submit_button_text || 'Submit')}
                </button>
              </div>
            </form>
          )}
          </div>
        </div>
      </div>
    </main>
  );
};

export default ContactFormPage;
